import { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, WifiOff, X } from 'lucide-react';
import logger from '../../utils/core/logger';
import { isNative } from '../../utils/core/platform';

const PWAHandler = () => {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [updateRegistration, setUpdateRegistration] = useState(null);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    // Listen for PWA events
    const handlePWAReady = (event) => {
      setShowSuccessBanner(true);
      setTimeout(() => setShowSuccessBanner(false), 5000);
    };

    const handlePWAUpdate = (event) => {
      setUpdateRegistration(event.detail.registration);
      setShowUpdateBanner(true);
    };

    const handleNetworkStatus = (event) => {
      setIsOffline(!event.detail.online);
    };

    window.addEventListener('pwa-ready', handlePWAReady);
    window.addEventListener('pwa-update', handlePWAUpdate);
    window.addEventListener('networkstatus', handleNetworkStatus);

    return () => {
      window.removeEventListener('pwa-ready', handlePWAReady);
      window.removeEventListener('pwa-update', handlePWAUpdate);
      window.removeEventListener('networkstatus', handleNetworkStatus);
    };
  }, []);

  // Periodic service worker update check
  useEffect(() => {
    if (isNative()) return;
    if (!('serviceWorker' in navigator)) return;

    const checkForUpdates = async () => {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (registration) {
          // Check for updates
          await registration.update();
          logger.info('Service Worker: Checked for updates');
        }
      } catch (error) {
        logger.error('Service Worker: Update check failed', error);
      }
    };

    // Check immediately on mount
    checkForUpdates();

    // Check every 60 seconds
    const updateCheckInterval = setInterval(checkForUpdates, 60000);

    return () => clearInterval(updateCheckInterval);
  }, []);

  const handleRefreshUpdate = () => {
    if (updateRegistration && updateRegistration.waiting) {
      updateRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });

      updateRegistration.waiting.addEventListener('statechange', (e) => {
        if (e.target.state === 'activated') {
          window.location.reload();
        }
      }, { once: true }); // once: true prevents duplicate handlers if user clicks multiple times
    }
  };

  const handleDismissUpdate = () => {
    setShowUpdateBanner(false);
  };

  // Skip all PWA UI in native apps (after all hooks to satisfy React rules)
  if (isNative()) return null;

  // Update banner
  if (showUpdateBanner) {
    return (
      <div
        className="fixed top-0 left-0 right-0 bg-[#ffb85e] text-[#1a1410] p-3 flex items-center justify-between z-50 shadow-lg"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="flex items-center gap-3">
          <RefreshCw size={20} className="text-white" />
          <div>
            <div className="font-medium text-sm">New version available!</div>
            <div className="text-xs opacity-90">Update to get the latest features and improvements</div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRefreshUpdate}
            className="px-3 min-h-[44px] bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors flex items-center justify-center"
          >
            Update
          </button>
          <button
            onClick={handleDismissUpdate}
            className="px-2 min-h-[44px] min-w-[44px] hover:bg-white/20 rounded text-xs transition-colors flex items-center justify-center"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Success banner
  if (showSuccessBanner) {
    return (
      <div
        className="fixed top-0 left-0 right-0 bg-[#ffb85e] text-[#1a1410] p-3 flex items-center justify-between z-50 shadow-lg"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="flex items-center gap-3">
          <CheckCircle size={20} className="text-white" />
          <div>
            <div className="font-medium text-sm">App is ready for offline use!</div>
            <div className="text-xs opacity-90">Your journal entries will be saved locally</div>
          </div>
        </div>
        <button
          onClick={() => setShowSuccessBanner(false)}
          className="px-2 min-h-[44px] min-w-[44px] hover:bg-white/20 rounded text-xs transition-colors flex items-center justify-center"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // Offline indicator
  if (isOffline) {
    return (
      <div
        className="fixed top-0 left-0 right-0 bg-orange-600 text-white p-2 flex items-center justify-center z-50 shadow-lg"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
      >
        <div className="flex items-center gap-2">
          <WifiOff size={16} className="text-white" />
          <span className="text-sm font-medium">You're offline - Your entries are saved locally</span>
        </div>
      </div>
    );
  }

  return null;
};

export default PWAHandler;